import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

interface Turma { id: number; nome: string; }
interface ItemChamada { id_aluno: number; nome: string; status: string | null; justificativa: string; registrado: boolean; }
interface LinhaRelatorio { id_aluno: number; nome: string; total: number; presencas: number; faltas: number; justificadas: number; frequenciaPct: number; }
interface Alertas { minimoPct: number; minimoDiasConsiderado: number; total: number; criancas: LinhaRelatorio[]; }

const STATUS: { valor: string; label: string; icone: string; cor: string }[] = [
  { valor: 'presente', label: 'Presente', icone: '✅', cor: '#2e7d32' },
  { valor: 'ausente', label: 'Ausente', icone: '❌', cor: '#c62828' },
  { valor: 'justificado', label: 'Justificado', icone: '📝', cor: '#e65100' },
  { valor: 'atrasado', label: 'Atrasado', icone: '⏰', cor: '#1565c0' },
];

/**
 * Data de HOJE no fuso de quem está usando — nunca via `toISOString()`.
 *
 * `toISOString()` converte para UTC: no Brasil, a partir das 21h ele devolve o
 * dia SEGUINTE, e a chamada abria na data errada justamente no fim do turno da
 * tarde. Mesma classe de bug do PR #82; a regra da casa é montar a data pelos
 * componentes locais.
 */
const hoje = () => {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
};

const primeiroDiaDoMes = () => hoje().slice(0, 8) + '01';

export default function PresencaPage() {
  const [turmas, setTurmas] = useState<Turma[]>([]);
  const [idTurma, setIdTurma] = useState<number>(0);
  const [data, setData] = useState<string>(hoje());
  const [chamada, setChamada] = useState<ItemChamada[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState('');
  const [erro, setErro] = useState('');

  // Relatório
  const [relInicio, setRelInicio] = useState<string>(primeiroDiaDoMes());
  const [relFim, setRelFim] = useState<string>(hoje());
  const [relTurma, setRelTurma] = useState<number>(0);
  const [relatorio, setRelatorio] = useState<LinhaRelatorio[]>([]);
  const [gerandoRel, setGerandoRel] = useState(false);

  // Alertas de infrequência
  const [alertas, setAlertas] = useState<Alertas | null>(null);

  useEffect(() => {
    api.get<Turma[]>('/turmas').then(({ data }) => {
      const lista = Array.isArray(data) ? data : [];
      setTurmas(lista);
      // Professor tem uma turma só: já entra com ela selecionada, sem obrigá-lo
      // a escolher todo dia o que nunca muda.
      if (lista.length === 1) setIdTurma(lista[0].id);
    }).catch(() => {});
  }, []);

  // O alerta é a razão de a tela existir para o gestor: ele abre e já vê quem
  // está sumindo, sem precisar gerar relatório e ler a tabela inteira.
  useEffect(() => {
    api.get<Alertas>(`/presenca/alertas?inicio=${primeiroDiaDoMes()}&fim=${hoje()}`)
      .then(({ data }) => setAlertas(data))
      .catch(() => setAlertas(null));
  }, []);

  const carregarChamada = useCallback(async () => {
    if (!idTurma || !data) { setChamada([]); return; }
    setCarregando(true); setErro(''); setMsg('');
    try {
      const { data: resp } = await api.get(`/presenca/chamada?data=${data}&id_turma=${idTurma}`);
      setChamada(resp.chamada || []);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar a chamada.');
    } finally { setCarregando(false); }
  }, [idTurma, data]);

  useEffect(() => { carregarChamada(); }, [carregarChamada]);

  const setStatus = (idAluno: number, status: string) =>
    setChamada((c) => c.map((i) => i.id_aluno === idAluno ? { ...i, status } : i));
  const setJustificativa = (idAluno: number, v: string) =>
    setChamada((c) => c.map((i) => i.id_aluno === idAluno ? { ...i, justificativa: v } : i));
  const marcarTodosPresentes = () =>
    setChamada((c) => c.map((i) => ({ ...i, status: i.status ?? 'presente' })));

  const salvar = async () => {
    const registros = chamada.filter((i) => i.status).map((i) => ({ id_aluno: i.id_aluno, status: i.status, justificativa: i.justificativa }));
    if (registros.length === 0) { setErro('Marque ao menos uma criança antes de salvar.'); return; }
    setSalvando(true); setErro(''); setMsg('');
    try {
      const { data: resp } = await api.post('/presenca/chamada', { data, id_turma: idTurma, registros });
      const ignorados = Array.isArray(resp.ignorados) ? resp.ignorados.length : 0;
      setMsg(`✅ Chamada salva — ${resp.gravados} registro(s).`);
      if (ignorados > 0) {
        // Acontece quando alguém troca a criança de turma com a tela aberta.
        setErro(`${ignorados} criança(s) não pertencem mais a esta turma e foram ignoradas. Recarregue a chamada.`);
      }
      carregarChamada();
      setTimeout(() => setMsg(''), 4000);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Falha ao salvar a chamada.');
    } finally { setSalvando(false); }
  };

  const gerarRelatorio = async () => {
    if (!relInicio || !relFim) { setErro('Informe o período do relatório.'); return; }
    setGerandoRel(true); setErro('');
    try {
      const q = `inicio=${relInicio}&fim=${relFim}${relTurma ? `&id_turma=${relTurma}` : ''}`;
      const { data: resp } = await api.get<LinhaRelatorio[]>(`/presenca/relatorio?${q}`);
      setRelatorio(Array.isArray(resp) ? resp : []);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Falha ao gerar o relatório.');
    } finally { setGerandoRel(false); }
  };

  const corFreq = (p: number) => (p >= 75 ? '#2e7d32' : p >= 50 ? '#e65100' : '#c62828');

  return (
    <div className="stack-cadastros" style={{ maxWidth: 960 }}>
      {/* ── ALERTA DE INFREQUÊNCIA ──
          Vem PRIMEIRO de propósito: é a informação que exige ação, e quem abre
          a tela precisa vê-la antes de qualquer outra coisa. */}
      {alertas && alertas.total > 0 && (
        <section style={{ background: '#fff4f4', border: '1px solid #f3c9c9', borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, color: '#c62828' }}>
            ⚠️ {alertas.total} criança(s) abaixo de {alertas.minimoPct}% de frequência neste mês
          </h3>
          <p style={{ margin: '0 0 12px', color: '#7a4a4a', fontSize: 14 }}>
            Falta prolongada costuma ter uma causa que a creche resolve com uma conversa —
            saúde, transporte, situação em casa. Da menor frequência para a maior:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {alertas.criancas.map((c) => (
              <div key={c.id_aluno} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#fff', border: '1px solid #f0dcdc', borderRadius: 8, padding: '8px 12px' }}>
                <span style={{ flex: '1 1 200px', fontWeight: 600 }}>{c.nome}</span>
                <span style={{ fontWeight: 800, color: corFreq(c.frequenciaPct), minWidth: 62 }}>{c.frequenciaPct}%</span>
                <span style={{ fontSize: 13, color: '#666' }}>
                  {c.faltas} falta(s){c.justificadas > 0 ? ` · ${c.justificadas} justificada(s)` : ''} em {c.total} dia(s)
                </span>
              </div>
            ))}
          </div>
          <p style={{ margin: '12px 0 0', color: '#8a6d6d', fontSize: 12.5 }}>
            Só entram crianças com pelo menos {alertas.minimoDiasConsiderado} dias registrados —
            antes disso o percentual ainda não significa nada.
          </p>
        </section>
      )}

      {/* ── CHAMADA DIÁRIA ── */}
      <section style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>📋 Chamada diária</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end', marginBottom: 12 }}>
          <label>Turma
            <select value={idTurma} onChange={(e) => setIdTurma(Number(e.target.value))} style={{ display: 'block' }}>
              <option value={0}>Selecione...</option>
              {turmas.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
            </select>
          </label>
          <label>Data
            <input type="date" value={data} onChange={(e) => setData(e.target.value)} style={{ display: 'block' }} />
          </label>
          {chamada.length > 0 && (
            <button onClick={marcarTodosPresentes} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cfe8d4', background: '#eaf6ec', cursor: 'pointer' }}>✅ Todos presentes</button>
          )}
        </div>

        {erro && <div style={{ color: '#c62828', marginBottom: 8 }}>{erro}</div>}
        {msg && <div style={{ color: '#2e7d32', marginBottom: 8 }}>{msg}</div>}

        {carregando ? <div>Carregando…</div> : !idTurma ? (
          <div style={{ color: '#888' }}>Selecione uma turma para fazer a chamada.</div>
        ) : chamada.length === 0 ? (
          <div style={{ color: '#888' }}>Nenhuma criança ativa nesta turma.</div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {chamada.map((i) => (
                <div key={i.id_aluno} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px 10px', border: '1px solid #f0f0f0', borderRadius: 8 }}>
                  <span style={{ flex: '1 1 180px', fontWeight: 600 }}>{i.nome}</span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {STATUS.map((s) => (
                      <button key={s.valor} onClick={() => setStatus(i.id_aluno, s.valor)} title={s.label}
                        style={{ padding: '5px 9px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                          border: i.status === s.valor ? `2px solid ${s.cor}` : '1px solid #ddd',
                          background: i.status === s.valor ? s.cor : '#fff', color: i.status === s.valor ? '#fff' : '#555' }}>
                        {s.icone}
                      </button>
                    ))}
                  </div>
                  {(i.status === 'ausente' || i.status === 'justificado' || i.status === 'atrasado') && (
                    <input placeholder="Justificativa" value={i.justificativa} onChange={(e) => setJustificativa(i.id_aluno, e.target.value)}
                      style={{ flex: '1 1 200px' }} />
                  )}
                </div>
              ))}
            </div>
            <button onClick={salvar} disabled={salvando} style={{ marginTop: 14, padding: '10px 20px', borderRadius: 8, border: 'none', background: salvando ? '#9cc7a2' : '#2e7d32', color: '#fff', fontWeight: 700, cursor: salvando ? 'default' : 'pointer' }}>
              {salvando ? 'Salvando…' : '💾 Salvar chamada'}
            </button>
          </>
        )}
      </section>

      {/* ── RELATÓRIO DE FREQUÊNCIA ── */}
      <section style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, marginTop: 20 }}>
        <h3 style={{ marginTop: 0 }}>📈 Relatório de frequência</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end', marginBottom: 12 }}>
          <label>De<input type="date" value={relInicio} onChange={(e) => setRelInicio(e.target.value)} style={{ display: 'block' }} /></label>
          <label>Até<input type="date" value={relFim} onChange={(e) => setRelFim(e.target.value)} style={{ display: 'block' }} /></label>
          <label>Turma (opcional)
            <select value={relTurma} onChange={(e) => setRelTurma(Number(e.target.value))} style={{ display: 'block' }}>
              <option value={0}>Todas</option>
              {turmas.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
            </select>
          </label>
          <button onClick={gerarRelatorio} disabled={gerandoRel} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#1565c0', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
            {gerandoRel ? 'Gerando…' : 'Gerar'}
          </button>
        </div>
        {relatorio.length === 0 ? (
          <div style={{ color: '#888' }}>Sem dados no período (gere o relatório).</div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead><tr style={{ background: '#fafafa', textAlign: 'left' }}>
                <th style={{ padding: '8px 12px' }}>Criança</th>
                <th style={{ padding: '8px 12px' }}>Dias</th>
                <th style={{ padding: '8px 12px' }}>Presenças</th>
                <th style={{ padding: '8px 12px' }}>Faltas</th>
                <th style={{ padding: '8px 12px' }}>Justif.</th>
                <th style={{ padding: '8px 12px' }}>Frequência</th>
              </tr></thead>
              <tbody>
                {relatorio.map((r) => (
                  <tr key={r.id_aluno} style={{ borderTop: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px 12px' }}>{r.nome}</td>
                    <td style={{ padding: '8px 12px' }}>{r.total}</td>
                    <td style={{ padding: '8px 12px' }}>{r.presencas}</td>
                    <td style={{ padding: '8px 12px', color: r.faltas > 0 ? '#c62828' : '#333' }}>{r.faltas}</td>
                    <td style={{ padding: '8px 12px' }}>{r.justificadas}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 700, color: corFreq(r.frequenciaPct) }}>{r.frequenciaPct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
